import React from "react";
import { Modal, Spin } from "antd";
import {
  getBookingEnginePaymentStatus,
} from "../../redux/HomePage/actionCreater";

// Online (prepaid) booking flow: the server stores the booking when the
// Razorpay order is created, the browser confirms it after Checkout and a
// server webhook finishes it if the browser never does. The open order id
// lives in sessionStorage so a reopened tab can pick the booking back up.
export const BE_ORDER_KEY = "be_order_id";
export const STATUS_POLL_INTERVAL = 3000;
export const MAX_STATUS_POLLS = 20;
// on resume, "created" this many times in a row = the guest never paid
const MAX_UNPAID_POLLS = 3;

export const getSavedOrderId = () => {
  try {
    return sessionStorage.getItem(BE_ORDER_KEY);
  } catch (err) {
    return null;
  }
};
export const saveOrderId = (orderId) => {
  try {
    sessionStorage.setItem(BE_ORDER_KEY, orderId);
  } catch (err) {}
};
export const clearSavedOrderId = () => {
  try {
    sessionStorage.removeItem(BE_ORDER_KEY);
  } catch (err) {}
};

// confirm / status response bodies
export const isReservationCreated = (response) =>
  response?.messageCode === "BOOKING_RESERVATION_CREATE" && !!response?.data;
const isOrderNotFound = (response) =>
  response?.messageCode === "BOOKING_PAYMENT_NOT_FOUND";
const pendingStatus = (response) =>
  response?.messageCode === "BOOKING_PAYMENT_PENDING"
    ? response?.data?.status
    : undefined;
// Razorpay has a payment for the order, the booking is being made / retried
export const isPaymentFound = (response) =>
  ["processing", "failed"].includes(pendingStatus(response));

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Polls the order status until the reservation exists. Resolves one of
// { reservation } | { notFound } | { notPaid } | { timedOut } | { cancelled }.
// notPaid only with stopWhenUnpaid (resume on load, where the guest may
// have closed the tab without paying).
export const pollBookingStatus = async ({
  dispatch,
  orderId,
  immediate = false,
  stopWhenUnpaid = false,
  isCancelled = () => false,
}) => {
  let unpaidInARow = 0;
  for (let attempt = 0; attempt < MAX_STATUS_POLLS; attempt++) {
    if (attempt > 0 || !immediate) {
      await wait(STATUS_POLL_INTERVAL);
    }
    if (isCancelled()) return { cancelled: true };
    const response = await dispatch(getBookingEnginePaymentStatus(orderId));
    if (isCancelled()) return { cancelled: true };
    if (isReservationCreated(response)) {
      return { reservation: response.data };
    }
    if (isOrderNotFound(response)) return { notFound: true };
    if (stopWhenUnpaid) {
      unpaidInARow =
        pendingStatus(response) === "created" ? unpaidInARow + 1 : 0;
      if (unpaidInARow >= MAX_UNPAID_POLLS) return { notPaid: true };
    }
  }
  return { timedOut: true };
};

// blocking "confirming" screen shown once the guest has paid; there is no
// way back to "Pay Now" from here
export function PaymentConfirming({ orderId, timedOut }) {
  return (
    <Modal
      open
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
    >
      <div style={{ textAlign: "center", padding: "16px 0" }}>
        {timedOut ? (
          <p style={{ fontSize: 16, marginBottom: 0 }}>
            Payment received. Your booking confirmation will be sent by email.
            Reference: <b>{orderId}</b>
          </p>
        ) : (
          <>
            <Spin size="large" />
            <p style={{ fontSize: 16, marginTop: 16, marginBottom: 0 }}>
              Payment received, confirming your booking…
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}
